import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const AdminPage = ({ user, onLogout }) => {
  const [settings, setSettings] = useState({ registration_enabled: true });
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [resetPasswordUserId, setResetPasswordUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, usersRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/settings`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/stats`)
      ]);
      
      setSettings(settingsRes.data);
      setUsers(usersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegistrationToggle = async (enabled) => {
    try {
      await axios.put(`${API}/admin/settings`, {
        registration_enabled: enabled
      });
      setSettings({ ...settings, registration_enabled: enabled });
      toast.success(`Registration ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserId) return;

    try {
      await axios.delete(`${API}/admin/users/${deleteUserId}`);
      toast.success('User deleted successfully');
      setDeleteUserId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleToggleAdmin = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/admin`);
      toast.success(`Admin access ${currentStatus ? 'revoked' : 'granted'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update admin status');
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUserId || !newPassword) return;

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await axios.put(`${API}/admin/users/${resetPasswordUserId}/reset-password`, {
        new_password: newPassword
      });
      toast.success('Password reset successfully');
      setResetPasswordUserId(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50">
        <div className="text-gray-600 text-xl font-medium">Loading admin panel...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-slate-600 to-gray-700 rounded-2xl flex items-center justify-center shadow-lg">
                <span className="text-2xl">⚙️</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
                <p className="text-sm text-gray-600">Manage users and settings</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => window.location.href = '/dashboard'}
                variant="outline"
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Back to Dashboard
              </Button>
              <Button
                onClick={onLogout}
                data-testid="logout-btn"
                variant="outline"
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-white/90 border-blue-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center">
                    <span className="text-2xl">👥</span>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-gray-900">{stats.total_users}</div>
                    <div className="text-sm text-gray-600">Total Users</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/90 border-green-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center">
                    <span className="text-2xl">📊</span>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-gray-900">{stats.total_sessions}</div>
                    <div className="text-sm text-gray-600">Total Sessions</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/90 border-purple-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center">
                    <span className="text-2xl">⏱️</span>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-gray-900">{stats.total_hours}h</div>
                    <div className="text-sm text-gray-600">Total Hours</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/90 border-orange-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center">
                    <span className="text-2xl">🆕</span>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-gray-900">{stats.recent_registrations}</div>
                    <div className="text-sm text-gray-600">New (7 days)</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="settings" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
            <TabsTrigger value="settings" data-testid="settings-tab">Settings</TabsTrigger>
            <TabsTrigger value="users" data-testid="users-tab">Users</TabsTrigger>
          </TabsList>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card className="bg-white/90 border-gray-200 shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl text-gray-900">Application Settings</CardTitle>
                <CardDescription className="text-gray-700">Configure global application settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="space-y-1">
                    <Label htmlFor="registration-toggle" className="text-base font-semibold text-gray-900">
                      User Registration
                    </Label>
                    <p className="text-sm text-gray-600">
                      Allow new users to create accounts
                    </p>
                  </div>
                  <Switch
                    id="registration-toggle"
                    data-testid="registration-toggle"
                    checked={settings.registration_enabled}
                    onCheckedChange={handleRegistrationToggle}
                  />
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex gap-3">
                    <div className="text-blue-600 text-xl">ℹ️</div>
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-1">Registration Status</h4>
                      <p className="text-sm text-blue-800">
                        {settings.registration_enabled
                          ? 'Registration is currently enabled. New users can create accounts.'
                          : 'Registration is currently disabled. Only existing users can log in.'}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="bg-white/90 border-gray-200 shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl text-gray-900">User Management</CardTitle>
                <CardDescription className="text-gray-700">
                  Manage user accounts ({users.length} total)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {users.map((u) => (
                    <div
                      key={u.id}
                      data-testid={`user-${u.id}`}
                      className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl border border-gray-200 hover:shadow-md transition-shadow"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-gray-900 text-lg">{u.name}</h3>
                          {u.is_admin && (
                            <Badge className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white">
                              Admin
                            </Badge>
                          )}
                          {u.id === user.id && (
                            <Badge variant="outline" className="border-blue-400 text-blue-700">
                              You
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div>Email: {u.email}</div>
                          <div>Joined: {new Date(u.created_at).toLocaleDateString()}</div>
                          <div className="flex gap-4">
                            <span>Sessions: {u.session_count}</span>
                            <span>Hours: {u.total_hours}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        {u.id !== user.id && (
                          <>
                            <Button
                              onClick={() => setResetPasswordUserId(u.id)}
                              data-testid={`reset-password-${u.id}`}
                              variant="outline"
                              size="sm"
                              className="border-blue-300 text-blue-700 hover:bg-blue-50"
                            >
                              Reset Password
                            </Button>
                            <Button
                              onClick={() => handleToggleAdmin(u.id, u.is_admin)}
                              data-testid={`toggle-admin-${u.id}`}
                              variant="outline"
                              size="sm"
                              className="border-purple-300 text-purple-700 hover:bg-purple-50"
                            >
                              {u.is_admin ? 'Remove Admin' : 'Make Admin'}
                            </Button>
                            <Button
                              onClick={() => setDeleteUserId(u.id)}
                              data-testid={`delete-user-${u.id}`}
                              variant="outline"
                              size="sm"
                              className="border-red-300 text-red-700 hover:bg-red-50"
                            >
                              Delete
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}

                  {users.length === 0 && (
                    <div className="text-center py-12 text-gray-600">
                      <div className="text-4xl mb-4">👥</div>
                      <p>No users found</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteUserId} onOpenChange={() => setDeleteUserId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this user and all their data (sessions, statistics).
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Dialog */}
      <AlertDialog open={!!resetPasswordUserId} onOpenChange={() => {
        setResetPasswordUserId(null);
        setNewPassword('');
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset User Password</AlertDialogTitle>
            <AlertDialogDescription>
              Enter a new password for this user. The password must be at least 6 characters long.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="new-password" className="text-gray-900">New Password</Label>
            <Input
              id="new-password"
              type="password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="mt-2"
              minLength={6}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setNewPassword('')}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetPassword}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={!newPassword || newPassword.length < 6}
            >
              Reset Password
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPage;
